/**
 * 65.22, the heavy machine gun (the owner, Oct 10: "50 cal works as a mental frame, and will be more powerful than
 * lmg"): models only — lane F builds the mechanism (one mounted bullet gun, shared with the 6×6's cupola, 63.1d step 3).
 *
 *   - `buildHeavyMg`: the gun alone — a heavy receiver with its feed cover and rear sight, a long barrel in its jacket
 *     with a carrying handle and a flash hider, the spade grips and their butterfly trigger at the back, the belt
 *     feeding in from an ammo can on the left. The emplacement mounts it on a tripod; the 6×6's cupola on its ring
 *     (vehicleModels.ts). Grip anchors for both hands and the muzzle.
 *   - `buildHmgModel`: the emplaced gun — a low tripod (two splayed front legs, a long rear leg the gunner straddles)
 *     with the traverse and the pitch, a ring of 63.2's sandbags two courses high with a firing gap at the front, spare
 *     cans inside. The gunner sits low behind it, both hands on the grips (`seat`, `gripR`, `gripL`; personModel's
 *     'ground' seat). Its far tier, and its wreck (`buildHmgWreck`); the damaged form is 63.4's pass on a fresh build.
 *
 * Built in person units (a person is 1.8) and scaled by `personHeight / 1.8`. Faces +Z. The person rig's hand targets
 * name its arms (armR at +X), so `gripR` is at +X and `gripL` at −X, and the belt feeds from the left grip's side.
 * Rig: root (scale) → full (the ring, the tripod's legs, the spare cans) → traverse (yaw, at the tripod's head) →
 * cradle (pitch) → the gun. Presentation only.
 */
import * as THREE from 'three';
import { REFERENCE } from '../constants';
import { bakeStaticParts, markShared } from './meshBake';
import type { UnitPalette } from './unitPalette';
import type { PersonTier } from './personModel';

/** The gun's and the emplacement's measures (person units). */
export const HMG = {
  /** the tripod's head above the ground, and the bore above the head (the gunner sits on the ground: a low gun) */
  head: 0.6, bore: 0.1,
  receiver: { w: 0.24, h: 0.22, d: 0.8 },
  barrel: { length: 1.14, r: 0.032 }, jacket: { length: 0.26, r: 0.056 }, hider: { length: 0.13, r: 0.05 },
  /** a fifty-cal can: width, height, length */
  can: [0.16, 0.2, 0.31] as const,
  /** the sandbag ring: its radius to the bags' centres, the firing gap either side of the front (radians) */
  ring: { radius: 1.35, gap: 0.42, courses: 2 },
  /** where the gunner sits: on the ground behind the grips, straddling the rear leg (personModel's 'ground' seat lifts the hips a hand off it) */
  seat: { z: -1.0 },
};

const matCache = new Map<string, THREE.MeshLambertMaterial>();
function mat(color: string): THREE.MeshLambertMaterial {
  let m = matCache.get(color);
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true })); matCache.set(color, m); }
  return m;
}
function box(parent: THREE.Object3D, w: number, h: number, d: number, color: string, x: number, y: number, z: number): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(color));
  m.position.set(x, y, z);
  parent.add(m);
  return m;
}
/** A cylinder along +Z (a barrel), centred at z. */
function tube(parent: THREE.Object3D, r0: number, r1: number, length: number, color: string, x: number, y: number, z: number, sides = 10): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.CylinderGeometry(r1, r0, length, sides), mat(color));
  m.rotation.x = Math.PI / 2; m.position.set(x, y, z);
  parent.add(m);
  return m;
}
/** A square bar from a to b. */
function bar(parent: THREE.Object3D, a: THREE.Vector3, b: THREE.Vector3, thick: number, color: string): THREE.Mesh {
  const d = b.clone().sub(a), m = new THREE.Mesh(new THREE.BoxGeometry(thick, d.length(), thick), mat(color));
  m.position.copy(a).add(b).multiplyScalar(0.5);
  m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.normalize());
  parent.add(m);
  return m;
}
/** A fifty-cal can standing at (x, z), its long side along `yaw`; `open` lifts its lid and hangs a belt end out. */
function can(parent: THREE.Object3D, p: UnitPalette, x: number, y: number, z: number, yaw: number, open = false): THREE.Group {
  const g = new THREE.Group(); g.position.set(x, y, z); g.rotation.y = yaw; parent.add(g);
  const [w, h, l] = HMG.can;
  box(g, w, h, l, p.dark, 0, h / 2, 0);
  box(g, w * 1.04, 0.025, l * 1.02, p.metal, 0, h + (open ? 0.06 : 0.012), open ? -l * 0.3 : 0).rotation.x = open ? -0.9 : 0;   // the lid
  box(g, 0.03, 0.02, l * 0.5, p.metal, w / 2 + 0.015, h * 0.7, 0);                                                              // the latch handle
  if (open) for (let i = 0; i < 5; i++) box(g, 0.1, 0.03, 0.035, '#b8923a', 0.0, h + 0.01 - i * 0.03, l / 2 + 0.02 + i * 0.03);   // a belt end over the lip
  return g;
}

export interface HeavyMg { group: THREE.Group; muzzle: THREE.Object3D; gripR: THREE.Object3D; gripL: THREE.Object3D }

/**
 * The gun alone, its bore along +Z from the cradle's pivot: the receiver behind the pivot, the barrel ahead, the grips
 * at the back for both hands, the belt from a can hung on the left (−X). Shared by the tripod and the 6×6's cupola.
 */
export function buildHeavyMg(p: UnitPalette): HeavyMg {
  const g = new THREE.Group();
  const R = HMG.receiver, B = HMG.barrel, J = HMG.jacket, H = HMG.hider;
  // the receiver: a heavy box, its feed cover on top, the rear sight leaf, the side plates' rivet strips
  box(g, R.w, R.h, R.d, p.metal, 0, 0, -0.1);
  box(g, R.w * 0.96, 0.05, R.d * 0.42, p.dark, 0, R.h / 2 + 0.025, 0.02);                      // the feed cover
  box(g, 0.05, 0.08, 0.03, p.dark, 0, R.h / 2 + 0.06, -0.38);                                 // the rear sight leaf
  for (const sx of [-1, 1]) box(g, 0.012, R.h * 0.5, R.d * 0.8, p.dark, sx * (R.w / 2 + 0.006), 0, -0.1);
  // the barrel: its jacket at the receiver's front, the long barrel, the carrying handle, the front sight, the flash hider —
  // marked thin (65.22, lane F): a hand's width, it stops neither a shot nor a look, so the measured hit shape leaves it out
  // (hitShapeFit.ts), as it leaves a building's furniture
  for (const thin of [
    tube(g, J.r, J.r, J.length, p.dark, 0, 0, 0.3 + J.length / 2),
    tube(g, B.r, B.r, B.length, p.metal, 0, 0, 0.3 + B.length / 2),
    box(g, 0.035, 0.1, 0.18, p.dark, 0, J.r + 0.05, 0.44),                                       // the carrying handle's post…
    box(g, 0.035, 0.03, 0.24, p.dark, 0, J.r + 0.11, 0.44),                                      // …and grip
    box(g, 0.02, 0.06, 0.02, p.dark, 0, B.r + 0.03, 0.3 + B.length - 0.1),                       // the front sight
    tube(g, H.r, H.r * 0.75, H.length, p.dark, 0, 0, 0.3 + B.length + H.length / 2, 8),          // the flash hider
  ]) thin.userData.thin = true;
  const muzzle = new THREE.Object3D(); muzzle.position.set(0, 0, 0.3 + B.length + H.length); g.add(muzzle);
  // the spade grips and the butterfly trigger between them, at the receiver's back
  const back = -0.1 - R.d / 2;
  box(g, R.w * 0.9, 0.05, 0.06, p.metal, 0, -0.02, back - 0.03);                               // the back plate's bridge
  const gripR = new THREE.Object3D(), gripL = new THREE.Object3D();
  for (const [sx, grip] of [[1, gripR], [-1, gripL]] as const) {
    const s = new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.022, 0.17, 8), mat(p.dark));
    s.position.set(sx * 0.09, -0.02, back - 0.1); g.add(s);
    box(g, 0.03, 0.03, 0.08, p.metal, sx * 0.09, 0.06, back - 0.06);                          // the grip's arm to the plate
    grip.position.set(sx * 0.09, -0.02, back - 0.1); g.add(grip);
  }
  box(g, 0.1, 0.05, 0.015, p.flash, 0, 0.02, back - 0.075);                                    // the butterfly trigger
  // the belt: up out of a can hung on the left (−X, the left grip's side), round into the feed slot
  can(g, p, -(R.w / 2 + 0.12), -0.24, -0.04, 0);
  const pts: THREE.Vector3[] = [];
  for (let i = 0; i <= 8; i++) {
    const t = i / 8;
    pts.push(new THREE.Vector3(-(R.w / 2 + 0.12 - 0.13 * t * t), -0.04 + 0.16 * Math.sin(t * Math.PI * 0.6), -0.04 + 0.02 * t));
  }
  for (let i = 0; i < pts.length - 1; i++) { const link = bar(g, pts[i], pts[i + 1], 0.03, '#b8923a'); link.scale.x = 3.2; }   // brass rounds, side by side
  return { group: g, muzzle, gripR, gripL };
}

export interface HmgModel {
  root: THREE.Group;
  full: THREE.Group;
  silhouette: THREE.Group;
  traverse: THREE.Group;     // rotation.y = yaw
  cradle: THREE.Group;       // rotation.x = −pitch
  muzzle: THREE.Object3D;
  /** where the gunner sits (traverse space: they swing with the gun): on the ground behind the grips — a PersonModel in
   * state 'seated' with seat 'ground' goes here, facing the traverse's +Z */
  seat: THREE.Object3D;
  gripR: THREE.Object3D;
  gripL: THREE.Object3D;
  tier: PersonTier;
}

/** The sandbag ring: two courses of 63.2's bags laid along the circle, in bond, a firing gap at the front. */
function sandbagRing(parent: THREE.Object3D, burst = 0, seed = 1): void {
  const s = REFERENCE.sandbag, { radius, gap, courses } = HMG.ring;
  const step = (s.width * 1.04) / radius, shades = ['#a89a78', '#9e9070', '#b0a280'];
  let k = 0;
  for (let c = 0; c < courses; c++) {
    for (let a = gap + (c % 2) * step * 0.5; a < Math.PI * 2 - gap - step * 0.4; a += step) {
      k++;
      const h = ((k * 2654435761 + seed * 97) >>> 0) / 4294967296;
      if (burst > 0 && h < burst * (c === courses - 1 ? 0.8 : 0.35)) {   // a burst bag: flattened, thrown a little out
        if (h < burst * 0.25) continue;
        const m = box(parent, s.width, s.height * 0.45, s.depth * 1.2, shades[k % 3], Math.sin(a) * (radius + 0.5 + h), s.height * 0.2, Math.cos(a) * (radius + 0.5 + h));
        m.rotation.set(0, a + h * 2, 0.2);
        continue;
      }
      const m = box(parent, s.width, s.height, s.depth, shades[k % 3], Math.sin(a) * radius, s.height * (c + 0.5), Math.cos(a) * radius);
      m.rotation.y = a;
      m.userData.sandbag = true;   // 65.22: the hit shape's ring is fitted round these (hitShapeFit.ts)
    }
  }
}

/** The emplaced heavy machine gun, `personHeight` world units a person (1.8 = person units). */
/** The cradle's block under the receiver (the live mount draws one too: gunLooks hmgLiveMount). */
export function hmgCradleBlock(parent: THREE.Object3D, p: UnitPalette): THREE.Mesh { return box(parent, 0.16, 0.06, 0.3, p.metal, 0, -0.08, -0.02); }

/** `gun`: the gun on its cradle (65.36: the host passes the look play draws, gunLooks buildPlayHmg). */
export function buildHmgModel(p: UnitPalette, personHeight: number, gun: HeavyMg = buildHeavyMg(p)): HmgModel {
  const root = new THREE.Group();
  root.scale.setScalar(personHeight / 1.8);
  const full = new THREE.Group(); root.add(full);
  sandbagRing(full);
  // spare cans inside the ring, behind and on the feed's side (−X); one open
  const thin: THREE.Object3D[] = [];
  thin.push(can(full, p, -0.75, 0, -0.55, 0.4), can(full, p, -0.92, 0, -0.32, 0.5), can(full, p, -0.62, 0, -0.85, 0.2, true));
  // the tripod: two splayed front legs and the long rear one, from the head down to their shoes
  const head = new THREE.Vector3(0, HMG.head, 0.05);
  for (const sx of [-1, 1]) thin.push(bar(full, head, new THREE.Vector3(sx * 0.55, 0, 0.6), 0.045, p.dark), box(full, 0.12, 0.025, 0.1, p.dark, sx * 0.55, 0.012, 0.6));
  thin.push(bar(full, head, new THREE.Vector3(0, 0, -0.95), 0.05, p.dark));
  thin.push(box(full, 0.1, 0.025, 0.14, p.dark, 0, 0.012, -0.95));
  thin.push(box(full, 0.5, 0.03, 0.04, p.metal, 0, 0.26, -0.48));                              // the traverse bar across the rear leg
  // 65.22 (lane F): the tripod's legs, their shoes, the bar and the loose cans stop neither a shot nor a look — left out
  // of the measured hit shape (hitShapeFit.ts), as the barrel is
  for (const t of thin) t.traverse(o => { o.userData.thin = true; });
  // the head: the traverse (yaw) and the cradle (pitch) the gun rides
  const traverse = new THREE.Group(); traverse.position.copy(head); full.add(traverse);
  const pintle = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.07, 0.1, 10), mat(p.metal)); pintle.position.y = 0.0; traverse.add(pintle);
  const cradle = new THREE.Group(); cradle.position.y = HMG.bore; traverse.add(cradle);
  hmgCradleBlock(cradle, p);                                                                   // the cradle under the receiver
  cradle.add(gun.group);
  // the gunner's hips: on the ground behind the grips, swinging with the traverse
  const seat = new THREE.Object3D(); seat.position.set(0, -HMG.head, HMG.seat.z); traverse.add(seat);
  // the far tier: the ring, the gun's bar, the tripod's head
  const silhouette = new THREE.Group(); root.add(silhouette);
  const ringFar = new THREE.Mesh(new THREE.CylinderGeometry(HMG.ring.radius + 0.3, HMG.ring.radius + 0.35, REFERENCE.sandbag.height * HMG.ring.courses, 12, 1, true), mat('#a89a78'));
  ringFar.position.y = (REFERENCE.sandbag.height * HMG.ring.courses) / 2; (ringFar.material as THREE.Material).side = THREE.DoubleSide; silhouette.add(ringFar);
  box(silhouette, 0.26, 0.26, 2.1, p.metal, 0, HMG.head + HMG.bore, 0.5);
  box(silhouette, 0.5, 0.5, 0.5, p.dark, 0, HMG.head * 0.5, 0);
  silhouette.visible = false;
  const model: HmgModel = { root, full, silhouette, traverse, cradle, muzzle: gun.muzzle, seat, gripR: gun.gripR, gripL: gun.gripL, tier: 'full' };
  aimHmg(model, 0, 0.05);
  return model;
}

/** Point the gun: yaw about the tripod's head, pitch positive up (radians; the tripod's stops). */
export function aimHmg(m: HmgModel, yaw: number, pitch: number): void {
  m.traverse.rotation.y = yaw;
  m.cradle.rotation.x = -Math.max(-0.5, Math.min(0.6, pitch));   // (65.22 (1): down to about the M3 tripod's depression — it fires over its ring now)
}

/** Fold the static parts per moving group (the base, the traverse, the cradle) and the far tier. */
export function bakeHmgModel(m: HmgModel): number {
  let n = bakeStaticParts(m.full, { exclude: [m.traverse] }).partsBaked;
  n += bakeStaticParts(m.traverse, { exclude: [m.cradle] }).partsBaked;
  n += bakeStaticParts(m.cradle).partsBaked;
  m.silhouette.visible = true;
  n += bakeStaticParts(m.silhouette).partsBaked;
  setHmgTier(m, m.tier);
  return n;
}

export function setHmgTier(m: HmgModel, tier: PersonTier): void {
  m.tier = tier;
  m.full.visible = tier === 'full';
  m.silhouette.visible = tier === 'silhouette';
}

/**
 * The wreck (63.4): the ring burst (bags flattened and thrown out, the top course mostly gone), the tripod knocked over
 * with the gun lying across the bags, the cans scattered — in the scorched palette the caller passes. Baked.
 */
export function buildHmgWreck(p: UnitPalette, personHeight: number, seed = 1, gun: HeavyMg = buildHeavyMg(p)): THREE.Group {
  const root = new THREE.Group();
  root.scale.setScalar(personHeight / 1.8);
  sandbagRing(root, 0.7, seed);
  const tip = new THREE.Group(); tip.position.set(0.2, 0.05, 0.1); tip.rotation.set(0.15, 0.7, 1.25); root.add(tip);   // over on its side
  const head = new THREE.Vector3(0, HMG.head, 0.05);
  for (const sx of [-1, 1]) bar(tip, head, new THREE.Vector3(sx * 0.55, 0, 0.6), 0.045, p.dark);
  bar(tip, head, new THREE.Vector3(0, 0, -0.95), 0.05, p.dark);
  gun.group.position.set(0, HMG.head + HMG.bore, 0); tip.add(gun.group);
  can(root, p, -1.0, 0, -0.2, 1.2, true); can(root, p, 0.9, 0.08, -0.9, 0.3);
  const c3 = can(root, p, -0.4, 0.08, 0.95, 2.0); c3.rotation.z = 1.4;
  bakeStaticParts(root);
  return root;
}
